import { toJid, type Jid } from "../xmpp/jid"
import type { Conversation, LastEvent } from "../types"
import { bearer, chatBaseUrl, chatFetch } from "./helpers"

type RawConversation = {
  peer: string
  type: "direct" | "group"
  // Optional so a pre-hydration Costanza (rows without name/archived) still
  // parses; the mapping defaults them.
  name?: string | null
  archived?: boolean
  // FAME-657. Optional so a Costanza too old to send them still parses.
  last_body?: string | null
  // FAME-703. A structured group event, mutually exclusive with last_body. Its
  // keys are already single words, so it maps across unchanged.
  last_event?: LastEvent | null
  last_retracted?: boolean
  last_attachment?: boolean
  members?: string[]
  // Empty on a row with no message yet — a conversation opened before its first
  // message (FAME-779), or a group before one. Mapped to "".
  last_msg_id: string | null
  last_ts: number
  unread: boolean
  // Absent means unmuted.
  muted?: boolean
}
/**
 * `GET /conversations` — the user's conversations, ordered by recency.
 */
export const getConversations = (sessionToken: string, signal?: AbortSignal) =>
  chatFetch<Conversation[], "json">({
    baseUrl: chatBaseUrl(),
    path: "/conversations",
    headers: bearer(sessionToken),
    responseType: "json",
    transform: (data) => (data as RawConversation[]).map(toConversation),
    signal,
  })

/**
 * `POST /conversations` — open a direct conversation with `peerJid` before its
 * first message (FAME-779). The peer's own list is untouched.
 *
 * Insert-only server-side: an existing conversation comes back as stored, not
 * resurfaced.
 */
export const createDirectConversation = (
  sessionToken: string,
  peerJid: Jid,
  signal?: AbortSignal
) =>
  chatFetch<Conversation, "json">({
    baseUrl: chatBaseUrl(),
    path: "/conversations",
    method: "POST",
    headers: bearer(sessionToken),
    data: { peer: peerJid },
    responseType: "json",
    transform: (data) => toConversation(data as RawConversation),
    signal,
  })

/**
 * `PUT /conversations/:peer/read` — clear the unread flag for one conversation,
 * server-side and across the user's devices. `peerJid` is the bare JID and is
 * URL-encoded into the path (e.g. `"32809@acme"` → `"32809%40acme"`).
 */
export const markConversationRead = (
  sessionToken: string,
  peerJid: Jid,
  signal?: AbortSignal
): Promise<void> =>
  chatFetch<void, "json">({
    baseUrl: chatBaseUrl(),
    path: `/conversations/${encodeURIComponent(peerJid)}/read`,
    method: "PUT",
    headers: bearer(sessionToken),
    signal,
  })

/**
 * `PUT` / `DELETE /conversations/:peer/mute` — mute or unmute one conversation
 * for this user. Idempotent; 404 when the user has no such conversation.
 */
export const setConversationMuted = (
  sessionToken: string,
  peerJid: Jid,
  muted: boolean,
  signal?: AbortSignal
): Promise<void> =>
  chatFetch<void, "json">({
    baseUrl: chatBaseUrl(),
    path: `/conversations/${encodeURIComponent(peerJid)}/mute`,
    method: muted ? "PUT" : "DELETE",
    headers: bearer(sessionToken),
    signal,
  })

const toConversation = (raw: RawConversation): Conversation => ({
  peer: toJid(raw.peer),
  // Anything the server doesn't explicitly tag as a group is a 1:1.
  type: raw.type === "group" ? "group" : "direct",
  name: raw.name ?? null,
  archived: raw.archived ?? false,
  // An empty body is no body, which is what a row with no message yet has.
  lastBody: raw.last_body || undefined,
  lastEvent: raw.last_event ?? undefined,
  lastRetracted: raw.last_retracted ?? false,
  lastAttachment: raw.last_attachment || undefined,
  members: raw.members,
  lastMessageId: raw.last_msg_id ?? "",
  lastTimestamp: raw.last_ts,
  unread: raw.unread,
  muted: raw.muted ?? false,
})
